import { useMutation } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import type {
  GenerateJobDescriptionRequestEnhanced,
  GenerateJobBenefitsRequest,
  GenerateJobRequirementsRequest,
  SuggestSkillsRequest,
  GenerateCandidateEmailRequest,
  GenerateSocialPostRequest,
  AIGenerationResponse,
  SocialPostResponse,
  ReadabilityScoreResponse,
} from '../../services/unified/types';

/**
 * Mutation hook for generating job description
 */
export const useGenerateJobDescription = () => {
  return useMutation({
    mutationFn: (data: GenerateJobDescriptionRequestEnhanced): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.generateJobDescription(data);
    },
    onError: (error) => {
      console.error('Job description generation failed:', error);
    },
  });
};

/**
 * Mutation hook for generating job benefits
 */
export const useGenerateJobBenefits = () => {
  return useMutation({
    mutationFn: (data: GenerateJobBenefitsRequest): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.generateJobBenefits(data);
    },
    onError: (error) => {
      console.error('Job benefits generation failed:', error);
    },
  });
};

/**
 * Mutation hook for generating job requirements
 */
export const useGenerateJobRequirements = () => {
  return useMutation({
    mutationFn: (data: GenerateJobRequirementsRequest): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.generateJobRequirements(data);
    },
    onError: (error) => {
      console.error('Job requirements generation failed:', error);
    },
  });
};

/**
 * Mutation hook for suggesting skills based on job title
 */
export const useSuggestSkills = () => {
  return useMutation({
    mutationFn: (data: SuggestSkillsRequest): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.suggestSkills(data);
    },
    onError: (error) => {
      console.error('Skills suggestion failed:', error);
    },
  });
};

/**
 * Mutation hook for generating candidate email
 */
export const useGenerateCandidateEmail = () => {
  return useMutation({
    mutationFn: (data: GenerateCandidateEmailRequest): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.generateCandidateEmail(data);
    },
    onError: (error) => {
      console.error('Candidate email generation failed:', error);
    },
  });
};

/**
 * Mutation hook for generating social media post
 */
export const useGenerateSocialPost = () => {
  return useMutation({
    mutationFn: (data: GenerateSocialPostRequest): Promise<SocialPostResponse> => {
      return unifiedApiService.ai.generateSocialPost(data);
    },
    onError: (error) => {
      console.error('Social media post generation failed:', error);
    },
  });
};

/**
 * Mutation hook for analyzing job description bias
 */
export const useAnalyzeBias = () => {
  return useMutation({
    mutationFn: (description: string): Promise<AIGenerationResponse> => {
      return unifiedApiService.ai.analyzeJobDescriptionBias(description);
    },
    onError: (error) => {
      console.error('Bias analysis failed:', error);
    },
  });
};

/**
 * Mutation hook for calculating readability score
 */
export const useCalculateReadability = () => {
  return useMutation({
    mutationFn: (text: string): Promise<ReadabilityScoreResponse> => {
      return unifiedApiService.ai.calculateReadabilityScore(text);
    },
    onError: (error) => {
      console.error('Readability calculation failed:', error);
    },
  });
};
